import React, { useEffect, useState } from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import { Intl } from "../../../state/helpers/IntlProps"
import { CreateSerialNumberPayload,
    PatchSerialNumberPayload,
    SerialNumberPayload
} from "../../../state/serial-number/SerialNumberPayload"
import SerialNumberService from "../../../state/serial-number/SerialNumberService"
import SerialNumber from "../../../state/serial-number/SerialNumber"
import { useMutation } from "react-query"
import { Form } from "react-bootstrap"
import { isEqual } from "lodash"
import { FormValidationProps } from "../../../state/form/FormValidation"
import { SerialNumberElastic } from "../../../state/listing/serial-numbers/models"

type Props = {
    intl: Intl
    serialNumberId?: string
    productId?: string
    serialNumber?: SerialNumberElastic
    formId: string
} & FormValidationProps<SerialNumber>

const SerialNumberFormComponent = (props: Props) => {
    const getInitialPayload = () => {
        const payload = {
            serial: props.serialNumber?.getSerial() || "",
        }

        if (props.serialNumberId) {
            return {
                ...payload,
                id: props.serialNumberId,
            }
        }

        return {
            ...payload,
            productId: props.productId || "",
        }
    }

    const initialPayload = getInitialPayload()
    const [serialNumberPayload, setSerialNumberPayload] = useState<SerialNumberPayload>(initialPayload)

    const { mutate: upsertSerialNumber, isLoading: isUpsertingSerialNumber } = useMutation((payload: SerialNumberPayload) => {
        const updatePayload = payload as PatchSerialNumberPayload

        if (updatePayload.id) {
            return SerialNumberService.patch(updatePayload)
        }

        const createPayload = payload as CreateSerialNumberPayload
        return SerialNumberService.create(createPayload)
    }, {
        onSuccess: (data: SerialNumber) => {
            props.onActionSuccessCallback(data)
        },
        onError: (error: Error) => {
            props.onActionErrorCallback(error)
        },
    })

    const saveSerialNumber = () => {
        upsertSerialNumber(serialNumberPayload)
    }

    const onFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault()

        setSerialNumberPayload({
            ...serialNumberPayload,
            [event.currentTarget.name]: event.currentTarget.value,
        })
    }

    const isFormDirty = (): boolean => {
        return !isEqual(serialNumberPayload, initialPayload)
    }

    useEffect(() => {
        let timeoutDelay = 0
        if (!isUpsertingSerialNumber) {
            timeoutDelay = 1200
        }

        setTimeout(() => {
            props.onActionLoadingCallback(isUpsertingSerialNumber)
        }, timeoutDelay)
    }, [isUpsertingSerialNumber])

    useEffect(() => {
        const form = document.getElementById(props.formId)
        if (form) {
            const serialNumberForm = form as HTMLFormElement
            const isFormValid = isFormDirty() && serialNumberForm.checkValidity()
            props.onFormValidityUpdateCallback(isFormValid)
        }
    }, [serialNumberPayload])

    return (
        <>
            <Form
                id={props.formId}
                className="py-1 px-3"
                onSubmit={(event) => {
                    event.preventDefault()

                    const form = event.currentTarget
                    const isFormValid = isFormDirty() && form.checkValidity()
                    props.onFormValidityUpdateCallback(isFormValid)

                    if (!isFormValid) {
                        event.stopPropagation()
                        return
                    }

                    saveSerialNumber()
                }}>
                <Form.Group>
                    <Form.Label htmlFor="serial">
                        <FormattedMessage
                            id="product.serial_number.form.serial_label"
                            defaultMessage="Serial"
                        />
                    </Form.Label>
                    <Form.Control
                        type="text"
                        name="serial"
                        value={serialNumberPayload.serial}
                        onChange={onFormChange}
                        required
                    />
                </Form.Group>
            </Form>
        </>
    )
}

export default injectIntl(SerialNumberFormComponent)


// WEBPACK FOOTER //
// ./src/product/serial-number/form/SerialNumberFormComponent.tsx